import { usePrintStudentWork } from '../../../library/printing/StudentWorkPrinter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Squad, User } from '../../../../types';
import { LevelLite } from '../../../curriculum/adapters/types';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { db } from '../../../../shared/storage/firebase';
import { createDateInZone } from '../../../../shared/utils/timezone';
import { startLevel, finishClass, cancelClass } from '../../../../services/firebase';
import AttendanceModal from '../AttendanceModal';
import FeedbackModal from '../FeedbackModal';
import {
  ResolvedSessionPlan,
  ResolvedSessionSlot,
  formatSlotDate,
} from '../../utils/sessionPlan';
import {
  ClassroomTeam,
  PortfolioEntry,
  summarizeEntriesForLevel,
  coalescePortfolioEntries,
  portfolioRevealReadiness,
  portfolioEntryMatchesSlot,
  recommendedRevealPlanIndex,
} from '../../classroom/types';
import { ClassroomTeamsService } from '../../classroom/ClassroomTeamsService';
import ClassroomStudentChip from './ClassroomStudentChip';
import TeamCard from './TeamCard';
import NameTeamModal from './NameTeamModal';
import UploadPhotosModal from './UploadPhotosModal';
import MissionsPanel from './MissionsPanel';
import BriefingModal from './BriefingModal';
import TeachersNotesModal from './TeachersNotesModal';
import ResourcesModal from './ResourcesModal';
import MissionNotesModal from './MissionNotesModal';
import { getCurriculumAdapterById } from '../../../curriculum/adapters/registry';
import { getSquadCurriculumId } from '../../utils/curriculum';
import {
  resolveLevelBriefing,
  resolveStageMission,
} from '../../classroom/missionLayers';
import RevealFlow from './RevealFlow';
import { LessonOutcomesDisplay } from './ClassesView';

interface BoardViewProps {
  squad: Squad;
  students: User[];
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
  plan: ResolvedSessionPlan;
  pagerIndex: number;
  onPagerChange: (index: number) => void;
  levels: LevelLite[];
  onOpenPortfolio: (teamId: string) => void;
}

function missionDone(
  squad: Squad,
  levelId: string,
  missionId: string
): boolean {
  const progress = squad.missionProgress?.[levelId]?.[missionId];
  if (typeof progress === 'boolean') return progress;
  if (typeof progress === 'number') return progress > 0;
  return false;
}

function extractDuration(text: string): string | null {
  const m = text.match(/\(([^)]*?\d+\s*min[^)]*)\)/i);
  return m ? m[1] : null;
}

function stripDuration(text: string): string {
  return text.replace(/\s*\([^)]*?\d+\s*min[^)]*\)\s*/gi, ' ').trim();
}

const BoardView: React.FC<BoardViewProps> = ({
  squad,
  students,
  teams,
  entries,
  plan,
  pagerIndex,
  onPagerChange,
  levels,
  onOpenPortfolio,
}) => {
  const { currentUser, userData } = useAuth();
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const printStudentWork = usePrintStudentWork();
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [notesMissionId, setNotesMissionId] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [outcomesOpen, setOutcomesOpen] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  const [nameModal, setNameModal] = useState<{
    mode: 'create' | 'rename';
    teamId?: string;
    initialName?: string;
    studentId?: string;
  } | null>(null);
  const [nameBusy, setNameBusy] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTeamId, setUploadTeamId] = useState<string | null>(null);

  const slot: ResolvedSessionSlot | undefined = plan.slots[pagerIndex];
  const level = levels.find((l) => l.id === slot?.levelId);
  const hasLessonOutcomes = Boolean(level && (level.thinkingSkill || level.refrain || level.iCanStatement || level.studentArtifactExample || level.aiGeneratedExample));
  const missions = useMemo(() => (level?.missions || []) as Array<{
    id: string;
    text: string;
    completed?: boolean;
    gameId?: string;
  }>, [level]);

  const firstIncompleteIdx = missions.findIndex(
    (m) => !missionDone(squad, slot?.levelId || '', m.id) && !m.completed
  );

  const isLive = slot?.status === 'live';
  const isPast = slot?.status === 'completed';
  const isFuture = slot?.status === 'future';
  const isNext = slot?.status === 'next';
  const classIsLive = squad.classStatus === 'current';
  /** Membership edits locked on past classes; photos still allowed */
  const membershipLocked = isPast;
  const startVisible = !classIsLive && isNext;
  const replayVisible = !classIsLive && isPast;
  const finishVisible = classIsLive && isLive;

  useEffect(() => setOutcomesOpen(false), [pagerIndex]);

  useEffect(() => {
    if (!finishVisible) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [finishVisible]);

  const liveStartedAt = squad.liveSession?.startTime
    ? (() => {
        try {
          const t = squad.liveSession.startTime as any;
          const date = typeof t?.toDate === 'function' ? t.toDate() : new Date(t);
          return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          });
        } catch {
          return null;
        }
      })()
    : null;

  const activeTeams = useMemo(
    () => teams.filter((t) => !t.archived),
    [teams]
  );

  const curriculumAdapter = useMemo(
    () => getCurriculumAdapterById(slot?.curriculumId || getSquadCurriculumId(squad)),
    [squad, slot?.curriculumId]
  );

  const getActivity = useCallback((missionId: string) =>
    missions.find((mission) => mission.id === missionId) || curriculumAdapter.getMissionActivity(missionId),
  [missions, curriculumAdapter]);

  const briefing = useMemo(() => {
    if (!level) {
      return resolveLevelBriefing(
        { id: '', title: 'Class', missions: [] },
        getActivity
      );
    }
    return resolveLevelBriefing(level, getActivity);
  }, [level, getActivity]);

  const resolvedMissions = useMemo(
    () =>
      missions.map((m) =>
        resolveStageMission(
          { id: m.id, text: m.text, gameId: m.gameId },
          getActivity(m.id)
        )
      ),
    [missions, getActivity]
  );

  const reflectionIndex = resolvedMissions.findIndex(mission => mission.missionType === 'reflect');
  const reflectionReached = reflectionIndex >= 0 && (firstIncompleteIdx === -1 || firstIncompleteIdx >= reflectionIndex);
  const plannedClassMinutes = resolvedMissions.reduce((total, mission) => total + (mission.plannedMinutes || 0), 0);
  const liveStartedMs = (() => {
    const value = squad.liveSession?.startTime as any;
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    return new Date(value).getTime();
  })();
  const scheduledStartMs = (() => {
    const date = slot?.date;
    const time = squad.schedule?.startTime;
    if (!date || !time || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(time)) return 0;
    try {
      const ms = createDateInZone(date, time, squad.timezone).getTime();
      return Number.isFinite(ms) ? ms : 0;
    } catch {
      return 0;
    }
  })();
  const countdownStartMs = scheduledStartMs || liveStartedMs;
  const elapsedMinutes = countdownStartMs ? Math.max(0, Math.floor((nowMs - countdownStartMs) / 60_000)) : 0;
  const minutesLeft = plannedClassMinutes ? Math.max(0, plannedClassMinutes - elapsedMinutes) : null;
  const wrappingUp = finishVisible && (reflectionReached || (minutesLeft !== null && minutesLeft <= 10));
  const presentCount = Object.values(squad.attendance || {}).filter(Boolean).length;
  const attendanceTaken = Boolean(squad.attendanceMetadata?.lastTaken);
  const levelSummary = summarizeEntriesForLevel(entries, slot?.levelId || '');
  const revealEntriesByPlanIndex = useMemo(() => plan.slots.map((planSlot) =>
    coalescePortfolioEntries(entries.filter((entry) => portfolioEntryMatchesSlot(entry, planSlot)))
  ), [entries, plan.slots]);
  const revealPlanIndex = recommendedRevealPlanIndex(entries, plan.slots, pagerIndex);
  const revealEntries = revealEntriesByPlanIndex[revealPlanIndex] || [];
  const revealReady = revealEntries.filter((entry) => portfolioRevealReadiness(entry) === 'ready_to_reveal').length;

  const notesMission = notesMissionId ? resolvedMissions.find((mission) => mission.id === notesMissionId) || null : null;

  const assignedIds = useMemo(() => {
    const set = new Set<string>();
    activeTeams.forEach((t) => t.memberIds.forEach((id) => set.add(id)));
    return set;
  }, [activeTeams]);

  const unassigned = students.filter((s) => !assignedIds.has(s.id));

  const studentsById = useMemo(() => {
    const map: Record<string, User> = {};
    students.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [students]);

  const openCreateFromDrop = (studentId: string) => {
    setNameModal({ mode: 'create', studentId });
  };

  const handleNameSubmit = async (name: string) => {
    if (!nameModal) return;
    setNameBusy(true);
    try {
      if (nameModal.mode === 'rename' && nameModal.teamId) {
        await ClassroomTeamsService.renameTeam(squad.id, nameModal.teamId, name);
      } else if (nameModal.mode === 'create' && nameModal.studentId) {
        await ClassroomTeamsService.createTeamWithStudent({
          squadId: squad.id,
          name,
          studentId: nameModal.studentId,
          existingTeams: teams,
          createdBy: currentUser?.uid,
        });
      } else if (nameModal.mode === 'create') {
        await ClassroomTeamsService.createTeam({
          squadId: squad.id,
          name,
          existingTeams: teams,
          createdBy: currentUser?.uid,
        });
      }
      setNameModal(null);
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not save team.');
    } finally {
      setNameBusy(false);
    }
  };

  const handleCreateDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDropTarget(null);
    if (membershipLocked) return;
    const studentId = e.dataTransfer.getData('text/plain');
    if (!studentId) return;
    openCreateFromDrop(studentId);
  };

  /** Briefing → startLevel → attendance */
  const commitStartClass = async () => {
    if (!currentUser || !slot) return;
    setLifecycleBusy(true);
    try {
      const teacherName = userData?.displayName || currentUser.displayName || 'Teacher';
      await startLevel(squad.id, slot.levelId, currentUser.uid, teacherName);
      setBriefingOpen(false);
      setTimeout(() => setAttendanceOpen(true), 400);
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not start lesson.');
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleStartClassClick = () => {
    setBriefingOpen(true);
  };

  const handleAttendanceConfirmed = async (
    selected: { id: string; name: string; avatar: string }[]
  ) => {
    if (!currentUser) return;
    const selectedIds = new Set(selected.map((s) => s.id));
    const attendance: Record<string, boolean> = {};
    students.forEach((s) => {
      attendance[s.id] = selectedIds.has(s.id);
    });
    try {
      await updateDoc(doc(db, 'squads', squad.id), {
        attendance,
        attendanceMetadata: {
          lastTaken: new Date().toISOString(),
          takenBy: currentUser.uid,
        },
        lastAttendanceUpdate: new Date(),
      });
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not save attendance.');
    }
    setAttendanceOpen(false);
  };

  const handleFinishAfterFeedback = async () => {
    setLifecycleBusy(true);
    try {
      await finishClass(squad.id);
      setFeedbackOpen(false);
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not finish lesson.');
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleCancelSession = async (payload: {
    reason: 'accidental' | 'otherTeacher' | 'preview' | 'other';
    otherText?: string;
  }) => {
    if (!currentUser) return;
    setLifecycleBusy(true);
    try {
      await cancelClass(squad.id, {
        teacherId: currentUser.uid,
        reason: payload.reason,
        reasonOther: payload.otherText,
      });
      setFeedbackOpen(false);
    } catch (err) {
      console.error(err);
      void window.appAlert('Could not cancel class.');
    } finally {
      setLifecycleBusy(false);
    }
  };

  return (
    <div className="cd-panel">
        <>
          {slot && (
            <div className="cd-pager cd-class-ribbon">
              <button
                type="button"
                className="cd-chev"
                disabled={pagerIndex <= 0}
                onClick={() => onPagerChange(pagerIndex - 1)}
                aria-label="Previous class"
              >
                ‹
              </button>

              {startVisible && (
                <button
                  type="button"
                  className="cd-btn cd-class-action cd-class-action-start"
                  onClick={handleStartClassClick}
                  disabled={lifecycleBusy}
                >
                  {lifecycleBusy ? 'Starting…' : 'Start lesson'}
                </button>
              )}

              {finishVisible && (
                <button
                  type="button"
                  className="cd-btn cd-class-action cd-class-action-finish"
                  onClick={() => setFeedbackOpen(true)}
                  disabled={lifecycleBusy}
                >
                  Finish lesson
                </button>
              )}

              <div className="cd-pager-mid">
                <div className="cd-pager-count">
                  Class {slot.index} of {plan.slots.length}
                </div>
                <div className="cd-pager-lesson">
                  {slot.levelTitle}
                  {isLive && (
                    <span className={`cd-pill ${wrappingUp ? 'cd-pill-wrap' : 'cd-pill-live'}`}>
                      {wrappingUp ? 'Wrapping up' : `Live${minutesLeft !== null ? ` · ${minutesLeft} min left` : liveStartedAt ? ` · ${liveStartedAt}` : ''}`}
                    </span>
                  )}
                  {isPast && (
                    <span className="cd-pill cd-pill-done">
                      Completed{slot.date ? ` · ${formatSlotDate(slot.date)}` : ''}
                    </span>
                  )}
                  {isNext && <span className="cd-pill cd-pill-next">Next class</span>}
                  {isFuture && (
                    <span className="cd-pill cd-pill-muted">
                      Upcoming{slot.date ? ` · ${formatSlotDate(slot.date)}` : ''}
                    </span>
                  )}
                </div>
              </div>

              {!finishVisible && <div className="cd-ribbon-support">
                <button type="button" className="cd-ribbon-link" onClick={() => setNotesOpen(true)}>
                  <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 3.75h9l3 3V20.25H6z"/><path d="M15 3.75v3h3M9 11h6M9 14.5h6"/></svg>
                  Overview
                </button>
                <button type="button" className="cd-ribbon-link" onClick={() => setResourcesOpen(true)}>
                  <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M3.75 6.75h6l1.5 2h9v10.5H3.75z"/></svg>
                  Worksheets
                </button>
              </div>}

              {finishVisible && !wrappingUp && <button type="button" className="cd-ribbon-status" onClick={() => setAttendanceOpen(true)} disabled={lifecycleBusy}>
                {attendanceTaken ? `✓ Attendance ${presentCount}/${students.length}` : 'Take attendance'}
              </button>}

              {wrappingUp && <span className="cd-ribbon-photo-status">
                <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7.5h3l1.5-2h7l1.5 2h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>
                {levelSummary.teamCountWithPhotos}/{activeTeams.length} teams have photos
              </span>}

              {briefing.slideDeckUrl && (
                <a className={`cd-btn cd-slides-link${finishVisible && !wrappingUp ? ' cd-btn-primary' : ''}`} href={briefing.slideDeckUrl} target="_blank" rel="noreferrer">
                  Open Slides ↗
                </a>
              )}

              {replayVisible && (
                <button
                  type="button"
                  className="cd-btn"
                  onClick={handleStartClassClick}
                  disabled={lifecycleBusy}
                  title="Replay this completed class"
                >
                  {lifecycleBusy ? 'Starting…' : 'Replay class'}
                </button>
              )}

              {finishVisible && <div className="cd-ribbon-more">
                <button type="button" className="cd-btn cd-ribbon-more-btn" aria-label="More class actions" aria-expanded={moreOpen} onClick={() => setMoreOpen(value => !value)}>•••</button>
                {moreOpen && <div className="cd-ribbon-menu">
                  <button type="button" onClick={() => { setNotesOpen(true); setMoreOpen(false); }}>Teacher’s notes</button>
                  <button type="button" onClick={() => { setResourcesOpen(true); setMoreOpen(false); }}>Worksheets</button>
                  <button type="button" onClick={() => { setAttendanceOpen(true); setMoreOpen(false); }}>Attendance</button>
                </div>}
              </div>}

              <button
                type="button"
                className="cd-chev"
                disabled={pagerIndex >= plan.slots.length - 1}
                onClick={() => onPagerChange(pagerIndex + 1)}
                aria-label="Next class"
              >
                ›
              </button>
            </div>
          )}

          {level && hasLessonOutcomes && <section className={`cd-board-outcomes${outcomesOpen ? ' is-open' : ''}`}>
            <button type="button" className="cd-board-outcomes-toggle" aria-expanded={outcomesOpen} onClick={() => setOutcomesOpen((open) => !open)}>
              <span className="cd-board-outcomes-chevron" aria-hidden="true">⌄</span>
              <span><strong>Lesson outcomes</strong><small>{level.thinkingSkill || 'Artifact, AI output, refrain, and learning goal'}</small></span>
            </button>
            {outcomesOpen && <div className="cd-frame-details"><LessonOutcomesDisplay level={level} /></div>}
          </section>}

          {isPast && (
            <div className="cd-banner cd-banner-past">
              Completed{slot?.date ? ` · ${formatSlotDate(slot.date)}` : ''} — read-only team
              edits; you can still add photos from Portfolios
            </div>
          )}
          {isFuture && (
            <div className="cd-banner cd-banner-future">
              Upcoming{slot?.date ? ` · ${formatSlotDate(slot.date)}` : ''} — preview (Start
              unlocks when this is the next class)
            </div>
          )}

          {unassigned.length > 0 && (
            <div
              className={`cd-tray ${dropTarget === 'tray' ? 'is-drop-target' : ''}`}
              onDragOver={(e) => {
                if (membershipLocked) return;
                e.preventDefault();
                setDropTarget('tray');
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={async (e) => {
                e.preventDefault();
                setDropTarget(null);
                if (membershipLocked) return;
                const studentId = e.dataTransfer.getData('text/plain');
                if (!studentId) return;
                try {
                  await ClassroomTeamsService.assignStudent({
                    squadId: squad.id,
                    studentId,
                    toTeamId: null,
                    teams,
                  });
                } catch (err) {
                  console.error(err);
                }
              }}
            >
              <span className="cd-tray-lbl">Waiting for a team:</span>
              {unassigned.map((s) => (
                <ClassroomStudentChip
                  key={s.id}
                  student={s}
                  squadId={squad.id}
                  variant="tray"
                  draggable={!membershipLocked}
                  absent={squad.attendance?.[s.id] === false}
                />
              ))}
              <span className="cd-tray-hint">↳ drag onto a team</span>
            </div>
          )}

          <div className="cd-board">
            <div>
              <div className="cd-col-label">Teams</div>
              {activeTeams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  studentsById={studentsById}
                  squadId={squad.id}
                  entries={entries}
                  levelId={slot?.levelId || '1'}
                  attendance={squad.attendance}
                  readOnly={membershipLocked}
                  dragOver={dropTarget === team.id}
                  onDragOverChange={setDropTarget}
                  onOpenPortfolio={onOpenPortfolio}
                  onRename={(t) =>
                    setNameModal({ mode: 'rename', teamId: t.id, initialName: t.name })
                  }
                  onUploadPhotos={(teamId) => {
                    setUploadTeamId(teamId);
                    setUploadOpen(true);
                  }}
                  teams={teams}
                />
              ))}

              <div
                className={`cd-newteam ${dropTarget === 'new' ? 'is-drop-target' : ''}`}
                onClick={() => {
                  if (membershipLocked) return;
                  setNameModal({ mode: 'create' });
                }}
                onDragOver={(e) => {
                  if (membershipLocked) return;
                  e.preventDefault();
                  setDropTarget('new');
                }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={handleCreateDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (membershipLocked) return;
                  if (e.key === 'Enter' || e.key === ' ') setNameModal({ mode: 'create' });
                }}
              >
                ＋ New team — drop a student here
              </div>
            </div>

            <div>
              <MissionsPanel
                squadId={squad.id}
                levelId={slot?.levelId || '1'}
                missions={missions}
                getActivity={getActivity}
                isDone={(missionId) =>
                  isPast ||
                  missions.find((m) => m.id === missionId)?.completed === true ||
                  missionDone(squad, slot?.levelId || '', missionId)
                }
                isNow={(missionId, idx) =>
                  isLive &&
                  !missionDone(squad, slot?.levelId || '', missionId) &&
                  idx === (firstIncompleteIdx < 0 ? -1 : firstIncompleteIdx)
                }
                canToggleComplete={isLive}
                extractDuration={extractDuration}
                stripDuration={stripDuration}
                onOpenNotes={setNotesMissionId}
                revealReadyLabel={revealReady > 0 ? `${revealReady} of ${activeTeams.length} teams ready · Class ${plan.slots[revealPlanIndex]?.index}` : null}
                onOpenReveal={() => setRevealOpen(true)}
              />
            </div>
          </div>
        </>

      <NameTeamModal
        isOpen={!!nameModal}
        mode={nameModal?.mode || 'create'}
        initialName={nameModal?.initialName || ''}
        busy={nameBusy}
        onClose={() => setNameModal(null)}
        onSubmit={handleNameSubmit}
      />

      <UploadPhotosModal
        isOpen={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadTeamId(null);
        }}
        squadId={squad.id}
        teams={teams}
        plan={plan}
        initialTeamId={uploadTeamId}
        initialSlotIndex={pagerIndex}
        liveSessionId={squad.liveSession?.sessionId || null}
        classStatus={squad.classStatus}
        currentLessonId={squad.currentLessonId}
      />

      <BriefingModal
        onPrint={() => { setBriefingOpen(false); printStudentWork(slot?.index); }}
        isOpen={briefingOpen}
        levelTitle={slot?.levelTitle || level?.title || 'Class'}
        briefing={briefing}
        level={level}
        busy={lifecycleBusy}
        onClose={() => setBriefingOpen(false)}
        onStart={commitStartClass}
      />

      <TeachersNotesModal
        isOpen={notesOpen}
        levelTitle={slot?.levelTitle || level?.title || 'Class'}
        briefing={briefing}
        onClose={() => setNotesOpen(false)}
      />

      <ResourcesModal onPrint={() => { setResourcesOpen(false); printStudentWork(slot?.index); }} isOpen={resourcesOpen} briefing={briefing} levelTitle={slot?.levelTitle || level?.title || 'Class'} onClose={() => setResourcesOpen(false)} />
      <MissionNotesModal
        mission={notesMission}
        onClose={() => setNotesMissionId(null)}
        onTakeReflectionPhotos={() => {
          setNotesMissionId(null);
          setUploadTeamId(null);
          setUploadOpen(true);
        }}
        squadAvatarUrl={squad.classroomAvatarUrl}
        teamAvatars={activeTeams.map(team=>({name:team.name,url:team.avatarUrl}))}
      />

      {revealOpen && slot && <RevealFlow
        squadId={squad.id}
        plan={plan}
        initialPlanIndex={revealPlanIndex}
        recommendedPlanIndex={revealPlanIndex}
        teachingPlanIndex={pagerIndex}
        squadAvatarUrl={squad.classroomAvatarUrl}
        teams={activeTeams}
        entries={entries}
        onClose={() => setRevealOpen(false)}
      />}

      <AttendanceModal
        isOpen={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        students={students.map((student) => ({
          id: student.id,
          name: student.displayName || 'Student',
          avatar:
            student.selectedMuse &&
            typeof student.selectedMuse === 'object' &&
            student.selectedMuse.img
              ? student.selectedMuse.img
              : '/default-avatar.png',
        }))}
        onConfirmAttendance={handleAttendanceConfirmed}
      />

      {feedbackOpen && currentUser && (
        <FeedbackModal
          squad={squad}
          isOpen={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          onSubmitted={handleFinishAfterFeedback}
          onCancelSession={handleCancelSession}
          teacherId={currentUser.uid}
          teacherName={userData?.displayName || currentUser.displayName || 'Teacher'}
          achievementProgress={{}}
          teams={teams}
          entries={entries}
          plan={plan}
          pagerIndex={pagerIndex}
        />
      )}
    </div>
  );
};

export default BoardView;
